/**
 * The double bezel: one outer frame holding exactly one plate.
 *
 * A flat card on a flat background reads as a div; a plate seated in a tray
 * reads as hardware. The frame material is the session product panel's own
 * paint, so a result card dropped into a deliverable belongs to the same object
 * family as the map, gantt and chart widgets already living there.
 *
 * Radius, padding, both gradients, the hairline and the lift are not
 * overridable, and the tray publishes the plate's rung to everything nested
 * inside it. That is the whole mechanism behind concentric geometry: nobody
 * downstream does arithmetic.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import { getFontFamily } from "@/theme/typography";
import { CresultcardRadiusProvider } from "./cresultcard.radius";
import {
  CRC_FIELD_GRID_STYLE,
  CRC_PLATE_BACKGROUND_COLOR,
  CRC_PLATE_BACKGROUND_IMAGE,
  CRC_PLATE_SHADOW,
  CRC_R_PLATE,
  CRC_R_TRAY,
  CRC_TEXT,
  CRC_TRAY_BACKGROUND_IMAGE,
  CRC_TRAY_PAD,
  CRC_TRAY_SHADOW,
} from "./cresultcard.tokens";
import "./Cresultcard.css";

interface CresultcardTrayProps {
  readonly children: ReactNode;
  readonly width?: number | string;
  /** The faint field grid behind the content. Off unless a surface asks. */
  readonly texture?: boolean;
}

export function CresultcardTray({
  children,
  width,
  texture = false,
}: CresultcardTrayProps): ReactElement {
  return (
    <div
      className="cresultcard cresultcard__tray"
      data-el="Cresultcard/Tray/Root"
      style={{
        width,
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
        padding: CRC_TRAY_PAD,
        borderRadius: CRC_R_TRAY,
        // Declared once, here, and inherited by every primitive below it. No
        // type step carries a family of its own.
        fontFamily: getFontFamily(),
        color: CRC_TEXT,
        backgroundImage: CRC_TRAY_BACKGROUND_IMAGE,
        boxShadow: CRC_TRAY_SHADOW,
      }}
    >
      <div
        data-el="Cresultcard/Tray/Surface"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          height: "100%",
          overflow: "hidden",
          borderRadius: CRC_R_PLATE,
          backgroundColor: CRC_PLATE_BACKGROUND_COLOR,
          backgroundImage: CRC_PLATE_BACKGROUND_IMAGE,
          boxShadow: CRC_PLATE_SHADOW,
        }}
      >
        {texture && (
          <div
            aria-hidden
            style={CRC_FIELD_GRID_STYLE}
          />
        )}
        <CresultcardRadiusProvider radius={CRC_R_PLATE}>
          {children}
        </CresultcardRadiusProvider>
      </div>
    </div>
  );
}
